import { useMutation } from '@tanstack/react-query';
import { useApiClientContext } from '../../surveys/context';
import { ICustomSurveyResponse, IOperationResultNoValue } from '../../surveys/models/Api';

/**
 * React-Query mutation hook for creating a new custom survey response **draft**.
 *
 * Hits `POST survey/custom/response/draft/?token={token}`.
 * The body carries only `contactInfo` — the BE returns a fresh draft `ICustomSurveyResponse`
 * whose `token` field is the new Draft-intent access token for subsequent update calls.
 */
export const useClientCreateCustomSurveyResponseDraftMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: IOperationResultNoValue) => void;
	onSuccess?: (data?: ICustomSurveyResponse) => void;
}) => {
	const { apiClient } = useApiClientContext();

	return useMutation({
		mutationFn: ({ body, token }: { body: Pick<ICustomSurveyResponse, 'contactInfo'>; token: string }) => {
			// `token` is a URL-safe JWT (base64url segments joined by dots), so it needs no
			// encodeURIComponent — and it must stay raw to match the sibling response hooks and the
			// finalize POST, all of which interpolate the same token unencoded.
			return apiClient.post<ICustomSurveyResponse>(`survey/custom/response/draft/?token=${token}`, body);
		},
		onError,
		onSuccess,
	});
};
